/**
 * React shell for the exploration bubble canvas (zoom chrome, context wiring, cleanup).
 * D3 rendering: ./render/renderBubbleView.ts
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useExplorationActions,
  useExplorationData,
  useExplorationState,
  useFilteredVolumes,
} from '../../context/ExplorationContext';
import { useContainerSize } from '../../hooks/useContainerSize';
import { useInert } from '../../hooks/useInert';
import { useDrillPush } from '../../hooks/useDrillPush';
import { useLatest } from '../../hooks/useLatest';
import { useExplorationTooltipHandlers } from '../../hooks/useExplorationTooltipHandlers';
import { traceExplorationCanvas } from '../../lib/explorationCanvasTrace';
import { isExplorationScopeEmptyFromCounts } from '../../lib/isExplorationCanvasScopeEmpty';
import { buildCappedExplorationHierarchy } from '../../lib/hierarchy/buildCappedExplorationHierarchy';
import { ensureVolumeVisible } from '../../lib/bubble/ensureVolumeVisible';
import { canDrillUp } from '../../lib/drill/canDrillUp';
import { planCanvasOutSameUrlFallback } from '../../lib/drill/canvasOutFallback';
import {
  emptyDrillLocation,
  isDrillLocationEmpty,
  structuralPathStrings,
} from '../../lib/drill/drillLocation';
import { volumesInCurrentScope } from '../../lib/sidebar/volumesInCurrentScope';
import type { HierarchyNode } from '../../types/exploration';
import {
  applyCanvasOverlays,
  applyInstanceSelection,
  applyVolumeSelection,
} from './bubbleEmphasis';
import { type BubbleZoomControls, renderBubbleView } from './render/renderBubbleView';
import type { BubbleZoomLevelInfo } from './render/bubbleRenderOptions';
import {
  clearBubbleCanvasDrillUpClass,
  syncBubbleCanvasDrillUpClass,
} from './render/bubbleCanvasCursor';
import {
  applyExplorationCanvasEmptyState,
  clearExplorationCanvasContent,
  explorationCanvasBottomRightClass,
  explorationCanvasEmptyFiltersMessage,
  explorationCanvasEmptyScopeMessage,
} from '../explorationCanvasChrome';
import PlusIcon from '~/shared/assets/icons/plus-light-full.svg';
import MinusIcon from '~/shared/assets/icons/minus-light-full.svg';
import FitIcon from '~/shared/assets/icons/expand-light-full.svg';
import HomeIcon from '~/shared/assets/icons/home.svg';
import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';

const overlayRenderRetries = 4;

function countVolumeLeavesForTrace(node: HierarchyNode | null): number {
  if (!node?.children?.length) return 0;
  let count = 0;
  for (const child of node.children) {
    if (child.type === 'volume') count += 1;
    else count += countVolumeLeavesForTrace(child);
  }
  return count;
}

function emptyStateTraceBranch(
  canvasEmpty: boolean,
  globalEmpty: boolean,
  scopeEmpty: boolean,
  hierarchyRoot: HierarchyNode | null
): string {
  if (!canvasEmpty) return 'render';
  if (globalEmpty) return 'globalEmpty';
  if (scopeEmpty) return 'scopeEmpty';
  if (!hierarchyRoot) return 'noHierarchy';
  return 'unknownEmpty';
}

function BubbleCanvas() {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const navigate = useNavigate();
  const { loading } = useExplorationData();
  const filtered = useFilteredVolumes();
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomChromeRef = useRef<HTMLDivElement>(null);
  const zoomControlsRef = useRef<BubbleZoomControls | null>(null);
  const zoomPctRef = useRef<HTMLParagraphElement>(null);
  const [canZoomIn, setCanZoomIn] = useState(true);
  const [canZoomOut, setCanZoomOut] = useState(true);
  useInert(zoomChromeRef, loading);
  const size = useContainerSize(containerRef);
  const handleZoomLevel = useCallback((info: BubbleZoomLevelInfo) => {
    if (zoomPctRef.current) {
      zoomPctRef.current.textContent = `${info.pct}%`;
    }
    setCanZoomIn((prev) => (prev === info.canZoomIn ? prev : info.canZoomIn));
    setCanZoomOut((prev) => (prev === info.canZoomOut ? prev : info.canZoomOut));
  }, []);
  const { showTooltip, hideTooltip } = useExplorationTooltipHandlers();

  const handleDrill = useDrillPush();

  const handleGoHome = useCallback(() => {
    actions.setFilters([]);
    actions.setDrillPath(emptyDrillLocation(), { historyMode: 'push' });
  }, [actions]);
  const atHome = isDrillLocationEmpty(state.drillPath) && state.filters.length === 0;

  // Same as browser Back: restore the previous URL snapshot (filters included)
  // via popstate → syncFromUrl. Peel only when there is no in-app visit to undo.
  // Guard against double-clicks and same-URL history entries that leave navigate(-1)
  // with no SYNC (UI unchanged, stack length stuck).
  const navBackPendingRef = useRef(false);
  const drillHistoryLenRef = useLatest(state.drillHistory.length);
  const selectedNodeRef = useLatest(state.selectedNode);
  const drillPathRef = useLatest(state.drillPath);
  const navBackFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    navBackPendingRef.current = false;
    if (navBackFallbackTimerRef.current) {
      clearTimeout(navBackFallbackTimerRef.current);
      navBackFallbackTimerRef.current = null;
    }
  }, [state.drillHistory, state.drillPath, state.filters, state.selectedNode]);

  useEffect(() => {
    return () => {
      if (navBackFallbackTimerRef.current) {
        clearTimeout(navBackFallbackTimerRef.current);
      }
    };
  }, []);

  const handleDrillUp = useCallback(() => {
    if (drillHistoryLenRef.current > 0) {
      if (navBackPendingRef.current) {
        return;
      }
      const lenBefore = drillHistoryLenRef.current;
      const paramsBefore = window.location.search;
      navBackPendingRef.current = true;
      navigate(-1);
      if (navBackFallbackTimerRef.current) {
        clearTimeout(navBackFallbackTimerRef.current);
      }
      navBackFallbackTimerRef.current = setTimeout(() => {
        navBackFallbackTimerRef.current = null;
        if (drillHistoryLenRef.current < lenBefore) {
          navBackPendingRef.current = false;
          return;
        }
        const paramsAfter = window.location.search;
        // URL changed → syncFromUrl will apply; peeling here would double-undo.
        if (paramsAfter !== paramsBefore) {
          navBackPendingRef.current = false;
          return;
        }
        // Volume focus: clear selection (keep path) — do not visit-pop to a bare instance.
        const fallback = planCanvasOutSameUrlFallback(selectedNodeRef.current);
        navBackPendingRef.current = false;
        if (fallback === 'clear-selection') {
          actions.clearSelection();
        } else {
          actions.drillUp();
        }
      }, 80);
      return;
    }
    actions.drillUp();
  }, [actions, navigate]);

  const innerKey = state.view === 'activation' ? 'activationStatus' : 'supportStatus';
  const canvasId = state.view === 'activation' ? 'canvas-activation' : 'canvas-exploration';

  const selectedInstanceId =
    state.selectedNode?.kind === 'instance' ? state.selectedNode.instanceId : null;

  const selectedVolumeId =
    state.selectedNode?.kind === 'volume' ? state.selectedNode.volume.ebsId : null;

  const getCanDrillUp = useCallback(
    () =>
      canDrillUp({
        drillPath: drillPathRef.current,
        selectedNode: selectedNodeRef.current,
        drillHistoryLength: drillHistoryLenRef.current,
      }),
    []
  );

  const getSelectedVolumeId = useCallback(() => {
    const node = selectedNodeRef.current;
    return node?.kind === 'volume' ? node.volume.ebsId : null;
  }, []);

  const getSelectedInstanceId = useCallback(() => {
    const node = selectedNodeRef.current;
    return node?.kind === 'instance' ? node.instanceId : null;
  }, []);

  const highlightRef = useLatest(state.highlight);

  const reapplyCanvasOverlays = useCallback(() => {
    const el = containerRef.current;
    if (!el) return false;
    return applyCanvasOverlays(el, {
      highlight: highlightRef.current,
      view: state.view,
      selectedInstanceId,
      selectedVolumeId,
    });
  }, [selectedInstanceId, selectedVolumeId, state.view]);

  const reapplyCanvasOverlaysRef = useLatest(reapplyCanvasOverlays);

  // Light reapply on hover-leave — full overlays would relayout chips and flash labels.
  const reapplyVolumeSelection = useCallback(() => {
    const el = containerRef.current;
    const id = getSelectedVolumeId();
    if (!el || !id) return;
    applyVolumeSelection(el, id, state.view);
  }, [getSelectedVolumeId, state.view]);

  const reapplyVolumeSelectionRef = useLatest(reapplyVolumeSelection);

  const reapplyInstanceSelection = useCallback(() => {
    const el = containerRef.current;
    const id = getSelectedInstanceId();
    if (!el || !id) return;
    applyInstanceSelection(el, id);
  }, [getSelectedInstanceId]);

  const reapplyInstanceSelectionRef = useLatest(reapplyInstanceSelection);

  const retryCanvasOverlaysAfterRender = useCallback(() => {
    let attemptsLeft = overlayRenderRetries;
    const tryApply = () => {
      if (reapplyCanvasOverlaysRef.current()) return;
      attemptsLeft -= 1;
      if (attemptsLeft > 0) requestAnimationFrame(tryApply);
    };
    tryApply();
  }, []);

  const retryCanvasOverlaysAfterRenderRef = useLatest(retryCanvasOverlaysAfterRender);

  // Selection-only updates skip canvas rebuild; keep out-cursor class in sync with canDrillUp.
  useEffect(() => {
    const el = containerRef.current;
    if (!el?.querySelector('.bubbles-main')) return;
    syncBubbleCanvasDrillUpClass(el, getCanDrillUp());
  }, [
    getCanDrillUp,
    selectedVolumeId,
    selectedInstanceId,
    state.drillPath,
    state.drillHistory.length,
  ]);

  const canvasDrillPath = state.drillPath;

  const canvasDrillPathKeys = useMemo(
    () => structuralPathStrings(canvasDrillPath, { groupByAccount: state.groupByAccount }),
    [canvasDrillPath, state.groupByAccount]
  );

  const scopeOpts = useMemo(
    () => ({
      secondary: state.secondary,
      location: canvasDrillPath,
      groupByAccount: state.groupByAccount,
      tagKey: state.tagKey,
      view: state.view,
    }),
    [state.secondary, canvasDrillPath, state.groupByAccount, state.tagKey, state.view]
  );

  const scopeVolumes = useMemo(
    () => volumesInCurrentScope(filtered, scopeOpts),
    [filtered, scopeOpts]
  );

  const drillIsActive = !isDrillLocationEmpty(canvasDrillPath);
  const scopeEmpty = isExplorationScopeEmptyFromCounts(
    filtered.length,
    drillIsActive ? 1 : 0,
    scopeVolumes.length
  );

  const hierarchyRoot = useMemo<HierarchyNode | null>(() => {
    if (!filtered.length || scopeEmpty) return null;
    return buildCappedExplorationHierarchy(
      filtered,
      {
        innerKey,
        secondary: state.secondary,
        tagKey: state.tagKey,
        location: canvasDrillPath,
        groupByAccount: state.groupByAccount,
      },
      state.overflowPages
    );
  }, [
    filtered,
    innerKey,
    state.secondary,
    state.tagKey,
    canvasDrillPath,
    state.groupByAccount,
    state.overflowPages,
    scopeEmpty,
  ]);

  // A volume picked out of `+ N More` must keep a bubble to carry the selection ring:
  // selecting it re-roots the canvas, which resets the overflow paging that revealed it.
  // Same reference back when nothing was hidden, so the render effect does not re-run.
  // `overflowPages` goes in so a `+ N More` built inside a hoisted branch stays
  // expandable — the main capping pass never reaches that far.
  const pinnedHierarchyRoot = useMemo(
    () =>
      hierarchyRoot
        ? ensureVolumeVisible(hierarchyRoot, selectedVolumeId, state.overflowPages)
        : null,
    [hierarchyRoot, selectedVolumeId, state.overflowPages]
  );

  const accountTotalSize = useMemo(
    () => filtered.reduce((s, v) => s + (v.size || 0), 0),
    [filtered]
  );

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const globalEmpty = !filtered.length;
    const canvasEmpty = globalEmpty || scopeEmpty || !pinnedHierarchyRoot;

    traceExplorationCanvas('empty-state effect', {
      branch: emptyStateTraceBranch(canvasEmpty, globalEmpty, scopeEmpty, pinnedHierarchyRoot),
      filteredCount: filtered.length,
      drillPath: canvasDrillPathKeys,
      scopeVolumeCount: scopeVolumes.length,
      rootChildCount: pinnedHierarchyRoot?.children?.length ?? 0,
      volumeLeafCount: countVolumeLeavesForTrace(pinnedHierarchyRoot),
    });

    if (!canvasEmpty || loading) return;

    zoomControlsRef.current = null;
    if (zoomPctRef.current) {
      zoomPctRef.current.textContent = '100%';
    }
    setCanZoomIn(false);
    setCanZoomOut(false);
    hideTooltip();
    clearBubbleCanvasDrillUpClass(el);
    applyExplorationCanvasEmptyState(
      el,
      scopeEmpty
        ? explorationCanvasEmptyScopeMessage
        : explorationCanvasEmptyFiltersMessage
    );
    return () => {
      clearBubbleCanvasDrillUpClass(el);
      clearExplorationCanvasContent(el);
    };
  }, [
    filtered.length,
    pinnedHierarchyRoot,
    hideTooltip,
    loading,
    scopeEmpty,
    scopeVolumes.length,
    state.drillPath,
    canvasDrillPath,
  ]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !size.w || !size.h) return;
    if (!filtered.length || scopeEmpty || !pinnedHierarchyRoot) return;

    traceExplorationCanvas('render effect start', {
      filteredCount: filtered.length,
      drillPath: canvasDrillPathKeys,
      scopeVolumeCount: scopeVolumes.length,
      rootChildCount: pinnedHierarchyRoot.children?.length ?? 0,
      volumeLeafCount: countVolumeLeavesForTrace(pinnedHierarchyRoot),
      width: size.w,
      height: size.h,
    });

    const cleanup = renderBubbleView(el, pinnedHierarchyRoot, {
      view: state.view,
      location: canvasDrillPath,
      drillPath: canvasDrillPathKeys,
      secondary: state.secondary,
      groupByAccount: state.groupByAccount,
      tagKey: state.tagKey,
      volumes: filtered,
      canvasId,
      onDrill: handleDrill,
      onDrillUp: handleDrillUp,
      getCanDrillUp,
      onExpandOverflow: actions.expandOverflow,
      onSelectVolume: actions.selectVolume,
      onSelectInstance: actions.selectInstance,
      onZoomLevel: handleZoomLevel,
      accountTotalSize,
      showTooltip,
      hideTooltip,
      zoomControlsRef,
      getSelectedVolumeId,
      reapplyVolumeSelection: () => reapplyVolumeSelectionRef.current(),
      getSelectedInstanceId,
      reapplyInstanceSelection: () => reapplyInstanceSelectionRef.current(),
      getSidebarHighlight: () => highlightRef.current,
      reapplySidebarHighlight: () => reapplyCanvasOverlaysRef.current(),
      onRenderComplete: () => retryCanvasOverlaysAfterRenderRef.current(),
    });

    return () => {
      traceExplorationCanvas('render effect cleanup');
      cleanup?.();
    };
  }, [
    pinnedHierarchyRoot,
    state.view,
    canvasDrillPathKeys,
    state.drillPath,
    state.secondary,
    state.groupByAccount,
    state.tagKey,
    filtered,
    scopeEmpty,
    scopeVolumes.length,
    canvasId,
    size,
    accountTotalSize,
    actions.setDrillPath,
    actions.drillUp,
    handleDrill,
    handleDrillUp,
    getCanDrillUp,
    getSelectedVolumeId,
    getSelectedInstanceId,
    actions.expandOverflow,
    actions.selectVolume,
    actions.selectInstance,
    showTooltip,
    hideTooltip,
    handleZoomLevel,
  ]);

  // Instance dim → volume selection → sidebar highlight. Layout changes re-apply via onRenderComplete.
  useEffect(() => {
    reapplyCanvasOverlays();
  }, [reapplyCanvasOverlays, state.highlight, state.view, selectedInstanceId, selectedVolumeId]);

  return (
    <>
      <div ref={containerRef} className="exploration-canvas bubble-canvas" />
      <div ref={zoomChromeRef} className={`${explorationCanvasBottomRightClass} flex gap-2`}>
        <Button
          type="button"
          variant="third"
          size="sm"
          onClick={handleGoHome}
          disabled={!loading && atHome}
          className="!size-10 !p-0"
          icon={<HomeIcon className="size-6" />}
          aria-label="Return to home"
        />
        <div className="flex items-center justify-between bg-white/95 border border-gray-300 rounded-lg overflow-hidden !h-10 min-w-[130px]">
          <Button
            type="button"
            variant="text"
            size="sm"
            className="!size-10 !p-0 !min-w-0 rounded-none"
            onClick={() => zoomControlsRef.current?.zoomOut()}
            disabled={!canZoomOut}
            aria-label="Zoom out"
            icon={<MinusIcon className="size-4" />}
          />
          <Typography
            ref={zoomPctRef}
            variant="p3-reg"
            aria-live="polite"
            aria-atomic="true"
            className="px-0.5"
          >
            100%
          </Typography>
          <Button
            type="button"
            variant="text"
            size="sm"
            className="!size-10 !p-0 !min-w-0 rounded-none"
            onClick={() => zoomControlsRef.current?.zoomIn()}
            disabled={!canZoomIn}
            aria-label="Zoom in"
            icon={<PlusIcon className="size-4" />}
          />
        </div>
        <Button
          type="button"
          variant="third"
          size="sm"
          onClick={() => zoomControlsRef.current?.zoomFit()}
          className="!size-10 !p-0"
          icon={<FitIcon className="size-5" />}
          aria-label="Reset zoom"
        />
      </div>
    </>
  );
}

export default BubbleCanvas;
